import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Button, Table, useTable } from "@repo/o37-ui";
import { Plus, Receipt, Building, Users } from "lucide-react";
import { HoldingsOneTimeCost, formatCents } from '../../types';
import OneTimeCostForm from '../../components/Holdings/OneTimeCostForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface OneTimeCostListProps {
  onDataChange?: () => void;
}

export default function OneTimeCostList({ onDataChange }: OneTimeCostListProps) {
  const [costs, setCosts] = useState<HoldingsOneTimeCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState<HoldingsOneTimeCost | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCosts();
  }, []);

  const loadCosts = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/one-time-costs`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const transformed: HoldingsOneTimeCost[] = data.map((c: any) => ({
          id: c.id,
          date: c.date,
          amountCents: c.amount_cents,
          category: c.category,
          description: c.description,
          vendorName: c.vendor_name,
          companyId: c.company_id,
          personId: c.person_id,
          receiptUrl: c.receipt_url,
          notes: c.notes,
          createdBy: c.created_by,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
          company: c.holdings_companies ? {
            id: c.holdings_companies.id,
            name: c.holdings_companies.name,
            status: c.holdings_companies.status
          } : undefined,
          person: c.holdings_people ? {
            id: c.holdings_people.id,
            name: c.holdings_people.name
          } : undefined
        }));
        // Sort by date descending
        transformed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setCosts(transformed);
      }
    } catch (e) {
      console.error('Failed to load one-time costs:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEdit = (cost: HoldingsOneTimeCost) => {
    setEditingCost(cost);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this cost?')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/one-time-costs/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadCosts();
        onDataChange?.();
      }
    } catch (e) {
      console.error('Failed to delete cost:', e);
    }
  };

  const columns = useMemo(() => [
    {
      id: "date",
      header: "Date",
      accessorFn: (row: HoldingsOneTimeCost) => row.date,
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => (
        <span className="text-neutral-900 font-medium">
          {formatDate(row.original.date)}
        </span>
      )
    },
    {
      id: "description",
      header: "Description",
      accessorFn: (row: HoldingsOneTimeCost) => row.description,
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => (
        <div className="py-1">
          <div className="font-medium text-neutral-900 truncate max-w-xs">
            {row.original.description}
          </div>
          {row.original.vendorName && (
            <div className="text-sm text-neutral-500">{row.original.vendorName}</div>
          )}
        </div>
      )
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row: HoldingsOneTimeCost) => row.amountCents,
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => (
        <span className="font-medium text-neutral-900">
          {formatCents(row.original.amountCents)}
        </span>
      )
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row: HoldingsOneTimeCost) => row.category,
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => (
        <span className="text-sm px-2 py-1 bg-neutral-100 text-neutral-700 rounded-full">
          {row.original.category}
        </span>
      )
    },
    {
      id: "assignedTo",
      header: "Assigned To",
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => {
        const cost = row.original;
        if (cost.company) {
          return (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Building className="w-3.5 h-3.5 text-neutral-400" />
              <span>{cost.company.name}</span>
            </div>
          );
        }
        if (cost.person) {
          return (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Users className="w-3.5 h-3.5 text-neutral-400" />
              <span>{cost.person.name}</span>
            </div>
          );
        }
        return <span className="text-neutral-400">-</span>;
      }
    },
    {
      id: "actions",
      header: "",
      size: 120,
      meta: {
        headerClassName: "text-right",
        cellClassName: "justify-end"
      },
      cell: ({ row }: { row: { original: HoldingsOneTimeCost } }) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs px-3"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleEdit(row.original);
            }}
            text="Edit"
          />
          <Button
            variant="outline"
            className="h-8 text-xs px-3 text-red-600 border-red-200 hover:bg-red-50"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleDelete(row.original.id);
            }}
            text="Delete"
          />
        </div>
      )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: costs,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: costs.length,
    resourceName: (plural) => `cost${plural ? "s" : ""}`,
  });

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCost(null);
  };

  const handleFormSaved = () => {
    handleFormClose();
    loadCosts();
    onDataChange?.();
  };

  if (loading) {
    return <p className="text-neutral-500">Loading one-time costs...</p>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-neutral-500">
          {costs.length} one-time cost{costs.length !== 1 ? 's' : ''}
        </div>
        <Button
          onClick={() => setShowForm(true)}
          icon={<Plus className="w-4 h-4" />}
          text="Add Cost"
        />
      </div>

      {/* Table */}
      {costs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Receipt className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No One-Time Costs Yet</h3>
            <p className="text-neutral-500 mb-6">Add your first expense to start tracking costs.</p>
            <Button
              onClick={() => setShowForm(true)}
              text="Add Cost"
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table {...tableProps} table={table} />
        </Card>
      )}

      {/* One-Time Cost Form Modal */}
      {showForm && (
        <OneTimeCostForm
          cost={editingCost}
          onClose={handleFormClose}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
